import { useState } from "react";
import { Minus, Plus, Sparkles, Leaf, Heart, Award, Clock, ShieldCheck, Star, Droplets, Sun, Smile, Zap, BadgeCheck, Flower2, Baby, Shield, Droplet } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { looksLikeHtml, sanitizeProductHtml } from "@/lib/productDescription";

const ICONS = {
  Sparkles, Leaf, Heart, Award, Clock, ShieldCheck, Star, Droplets, Sun, Smile, Zap, BadgeCheck, Flower2, Baby, Shield, Droplet,
};
const FAQ_TONES = ["#F8EEEE", "#E7F6EF", "#FBF6E8", "#F8EEEE", "#E8F1FB", "#F3F4F6"];

function IconByName({ name, className }) {
  const C = ICONS[name] || Sparkles;
  return <C className={className} />;
}

function Rich({ html }) {
  if (!html) return <p className="text-sm text-stone-500">Nothing added for this tab yet.</p>;
  if (looksLikeHtml(html)) {
    return <div className="product-desc-prose text-stone-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: sanitizeProductHtml(html) }} />;
  }
  return <p className="text-stone-700 leading-relaxed whitespace-pre-wrap">{html}</p>;
}

function ReadMore({ children, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={testId}>
      <div className={open ? "" : "max-h-56 overflow-hidden relative"}>
        {children}
        {!open ? <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white to-transparent" /> : null}
      </div>
      <button type="button" className="mt-2 text-sm font-semibold text-blue-600" data-testid={`${testId}-toggle`} onClick={() => setOpen((v) => !v)}>
        {open ? "Read Less" : "Read More"}
      </button>
    </div>
  );
}

export function CosmosBestsellerTags({ tags }) {
  const rows = (tags || []).map((tag) => (typeof tag === "string" ? { text: tag, bg_color: "#DC2626", text_color: "#FFFFFF" } : tag)).filter((tag) => tag?.text);
  if (!rows.length) return null;
  return (
    <div className="mb-2 flex flex-wrap gap-1.5" data-testid="cosmos-bestseller-tags">
      {rows.map((tag, idx) => (
        <span key={`${tag.text}-${idx}`} className="inline-flex items-center rounded-sm px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide" style={{ background: tag.bg_color || "#DC2626", color: tag.text_color || "#FFFFFF" }} data-testid="cosmos-bestseller-tag">{tag.text}</span>
      ))}
    </div>
  );
}

export function CosmosCreativeInfo({ groups, primary }) {
  if (!groups?.length) return null;
  return (
    <div className="mt-5 rounded-2xl border border-stone-200 p-4 space-y-3" data-testid="cosmos-creative-info">
      {groups.map((group) => (
        <div key={group.title} className="grid grid-cols-[4.5rem_1fr] sm:grid-cols-[5.5rem_1fr] gap-3 items-start">
          <div className="text-sm text-stone-500 pt-1.5">{group.title}</div>
          <div className="flex flex-wrap gap-2">
            {(group.items || []).map((item) => (
              group.tone === "solid" ? (
                <span key={item.text} className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white" style={{ background: primary || "#2563EB" }}>{item.text}</span>
              ) : (
                <span key={item.text} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-stone-200 bg-white text-xs font-medium text-stone-700">
                  <IconByName name={item.icon} className="w-3.5 h-3.5 text-stone-500" />
                  {item.text}
                </span>
              )
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function CosmosDescriptionTabs({ product, design, primary }) {
  const tabs = [
    { id: "description", label: "Description" },
    { id: "why", label: "Why Choose Us?" },
    { id: "ingredients", label: "Ingredients List" },
    { id: "howto", label: "How To Use" },
    { id: "extra", label: "Additional Information" },
  ];
  const [tab, setTab] = useState("description");
  const ink = primary || "#2563EB";
  return (
    <section className="mt-12" data-testid="cosmos-description-tabs">
      <div className="flex flex-wrap gap-1 rounded-xl bg-stone-200/70 p-1">
        {tabs.map((item) => {
          const on = tab === item.id;
          return (
            <button
              key={item.id}
              type="button"
              data-testid={`cosmos-tab-${item.id}`}
              onClick={() => setTab(item.id)}
              className={`px-3 sm:px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${on ? "text-white shadow-sm" : "text-stone-600 hover:text-stone-900"}`}
              style={on ? { background: ink } : undefined}
            >{item.label}</button>
          );
        })}
      </div>
      <div className="pt-6" data-testid={`cosmos-panel-${tab}`}>
        {tab === "description" ? (
          <ReadMore testId="cosmos-desc-more"><Rich html={product.description} /></ReadMore>
        ) : null}
        {tab === "why" ? (
          <ReadMore testId="cosmos-why-more"><Rich html={design.why_choose_us} /></ReadMore>
        ) : null}
        {tab === "ingredients" ? (
          (design.ingredients || []).length ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {design.ingredients.map((row) => (
                <article key={row.name} className="rounded-xl overflow-hidden" data-testid="cosmos-ingredient-card">
                  <div className="aspect-[4/3] bg-stone-100">
                    {row.image_url ? <img src={mediaUrl(row.image_url)} alt={row.name} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-stone-100 to-stone-200" />}
                  </div>
                  <div className="pt-3 text-sm font-bold uppercase tracking-wide" style={{ color: ink }}>{row.name}</div>
                  {row.text ? <p className="mt-1 text-sm text-stone-600">{row.text}</p> : null}
                </article>
              ))}
            </div>
          ) : <p className="text-sm text-stone-500">No ingredients added yet.</p>
        ) : null}
        {tab === "howto" ? (
          (design.how_to_use || []).length ? (
            <ol className="space-y-4">
              {design.how_to_use.map((step, i) => (
                <li key={`${step.title}-${i}`} className="flex gap-4 pb-4 border-b border-stone-200 last:border-0" data-testid="cosmos-step">
                  <span className="w-10 h-10 rounded-full text-white text-sm font-bold flex items-center justify-center shrink-0" style={{ background: ink }}>{i + 1}</span>
                  <p className="pt-2 text-sm text-stone-700"><span className="font-bold text-stone-900">{step.title}:</span> {step.text}</p>
                </li>
              ))}
            </ol>
          ) : <p className="text-sm text-stone-500">No steps added yet.</p>
        ) : null}
        {tab === "extra" ? (
          (design.additional_info || []).length ? (
            <dl className="space-y-5">
              {design.additional_info.map((row) => (
                <div key={row.label} data-testid="cosmos-extra-row">
                  <dt className="text-sm font-bold text-stone-900">{row.label}:</dt>
                  <dd className="mt-1 text-sm text-stone-600 whitespace-pre-wrap">{row.value}</dd>
                </div>
              ))}
            </dl>
          ) : <p className="text-sm text-stone-500">No additional information yet.</p>
        ) : null}
      </div>
    </section>
  );
}

export function CosmosProductFaq({ faqs, primary }) {
  const items = faqs || [];
  const [open, setOpen] = useState(0);
  if (!items.length) return null;
  const ink = primary || "#2563EB";
  return (
    <section className="mt-12" data-testid="cosmos-product-faq">
      <h2 className="font-display text-2xl sm:text-3xl font-bold text-stone-900">Frequently Asked Questions</h2>
      <div className="mt-5 grid md:grid-cols-2 gap-3">
        {items.map((row, i) => {
          const on = open === i;
          return (
            <article key={`${row.question}-${i}`} className="rounded-2xl p-4" style={{ background: FAQ_TONES[i % FAQ_TONES.length] }} data-testid={`cosmos-faq-${i}`}>
              <button type="button" className="w-full flex items-start gap-3 text-left" data-testid={`cosmos-faq-toggle-${i}`} onClick={() => setOpen(on ? -1 : i)}>
                <span className="flex-1 text-sm font-semibold text-stone-900">{i + 1}. {row.question}</span>
                <span className="w-7 h-7 rounded-full text-white flex items-center justify-center shrink-0" style={{ background: ink }}>
                  {on ? <Minus className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                </span>
              </button>
              {on ? <p className="mt-3 pr-10 text-sm text-stone-600 leading-relaxed" data-testid={`cosmos-faq-answer-${i}`}>{row.answer}</p> : null}
            </article>
          );
        })}
      </div>
    </section>
  );
}
